import React from 'react'
import PropTypes from 'prop-types'

import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
} from '@material-ui/core'
import { fade } from '@material-ui/core/styles/colorManipulator'
import { withStyles } from '@material-ui/core/styles'

import AccessListEmptyState from './AccessListEmptyState'
import AccessUserEditLink from './AccessUserEditLink'
import DeleteUsersButton from './DeleteUsersButton'
import DisableUsersButton from './DisableUsersButtonContainer'
import EnableUsersButton from './EnableUsersButtonContainer'
import GrantDoorAccessButton from './GrantDoorAccessButtonContainer'
import CommunityAccessChip from './CommunityAccessChipContainer'
import CreateDoorAccessButton from './CreateDoorAccessButton'
import UnitAccessChip from './UnitAccessChipContainer'

class CommunityDoorAccessList extends React.Component {
  static propTypes = {
    /* HOC/middleware props */
    classes: PropTypes.object.isRequired,
    /* passed props */
    accessIds: PropTypes.array.isRequired,
    checkedIds: PropTypes.array.isRequired,
    communityId: PropTypes.number.isRequired,
    onToggleCheckAll: PropTypes.func.isRequired,
    onToggleCheckbox: PropTypes.func.isRequired,
    rows: PropTypes.array.isRequired,
  }

  static styles = (theme) => ({
    paper: {
      padding: '0 0 20px 0',
      marginTop: theme.spacing(3),
    },
    highlighted: {
      backgroundColor: fade('#000', 0.04),
    },
    toolbarHighlighted: {
      backgroundColor: fade(theme.palette.primary.main, 0.12),
    },
    toolbarControls: {
      flex: '1',
      textAlign: 'right',
    },
    table: {
      width: 'auto',
      margin: '0 24px',
      '& td': {
        padding: 0,
        '&:last-child': {
          paddingRight: 0,
        },
      },
      '& th': {
        padding: 0,
        '&.headerText': {
          padding: '0 10px',
        },
      },
    },
    noDoorAccess: {
      color: 'silver',
    },
  })

  renderRowDoorAccess = (row) => {
    const { classes, communityId } = this.props
    const { accessId, hasCommunityAccess, unitIds } = row

    if (!hasCommunityAccess && !unitIds.length) {
      return <span className={classes.noDoorAccess}>(no door access)</span>
    }

    return (
      <>
        { hasCommunityAccess
          && (
            <CommunityAccessChip
              key={`${accessId}-${communityId}`}
              accessId={accessId}
              communityId={communityId}
            />
          )}
        { unitIds.map((unitId) => (
          <UnitAccessChip
            key={`${accessId}-${unitId}`}
            accessId={accessId}
            unitId={unitId}
          />
        ))}
      </>
    )
  }

  renderRow = (row) => {
    const { checkedIds, classes } = this.props
    const { accessId } = row
    const isChecked = checkedIds.includes(accessId)

    return (
      <TableRow key={accessId} className={isChecked ? classes.highlighted : ''}>
        <TableCell>
          <Checkbox
            color="primary"
            checked={isChecked}
            onChange={(_, checked) => this.props.onToggleCheckbox(checked, accessId)}
          />
        </TableCell>
        <TableCell>
          <AccessUserEditLink accessId={accessId} />
        </TableCell>
        <TableCell align="right">
          { this.renderRowDoorAccess(row) }
        </TableCell>
        <TableCell>
          <GrantDoorAccessButton accessIds={[accessId]} />
        </TableCell>
      </TableRow>
    )
  }

  renderTable = () => {
    const {
      accessIds,
      checkedIds,
      classes,
      rows,
    } = this.props
    const isAllChecked = (checkedIds.length === accessIds.length)
    const isAllCheckedIndeterminate = (checkedIds.length > 0 && !isAllChecked)

    if (rows.length) {
      return (
        <Table classes={{ root: classes.table }}>
          <colgroup>
            <col width="1%" />
            <col />
            <col width="50%" />
            <col width="1%" />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell>
                <Checkbox
                  checked={isAllChecked}
                  onChange={this.props.onToggleCheckAll}
                  indeterminate={isAllCheckedIndeterminate}
                />
              </TableCell>
              <TableCell className="headerText">
                User
              </TableCell>
              <TableCell align="right" colSpan={2} className="headerText">
                Door Access
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            { rows.map((row) => this.renderRow(row)) }
          </TableBody>
        </Table>
      )
    }

    return (
      <AccessListEmptyState>
        <div>Looks like nobody has door access in this community.</div>
        <div>Create a door access user here.</div>
      </AccessListEmptyState>
    )
  }

  renderTitleToolbar = () => {
    const { classes, checkedIds, communityId } = this.props
    const checkedCount = checkedIds.length

    if (checkedCount > 0) {
      return (
        <Toolbar className={classes.toolbarHighlighted}>
          <Typography variant="subtitle1">
            {checkedCount} selected
          </Typography>
          <div className={classes.toolbarControls}>
            <DisableUsersButton accessIds={checkedIds} communityId={communityId} />
            <EnableUsersButton accessIds={checkedIds} communityId={communityId} />
            <DeleteUsersButton accessIds={checkedIds} communityId={communityId} />
            <GrantDoorAccessButton accessIds={checkedIds} />
          </div>
        </Toolbar>
      )
    }

    return (
      <Toolbar>
        <Typography variant="h6">
          Door Access
        </Typography>
        <div className={classes.toolbarControls}>
          <CreateDoorAccessButton communityId={communityId} />
        </div>
      </Toolbar>
    )
  }

  render() {
    return (
      <Paper className={this.props.classes.paper} elevation={0}>
        { this.renderTitleToolbar() }
        { this.renderTable() }
      </Paper>
    )
  }
}

export default withStyles(CommunityDoorAccessList.styles)(CommunityDoorAccessList)
